import { useEffect } from "react";
import { constant } from "../utils/constant";

// Captured once at import, before any page has overwritten it: the site-wide
// description that ships in index.html. A page that has no description of its
// own restores this instead of inheriting whatever the previous page set —
// <head> is a single shared node, so without a reset the last value simply
// sticks around.
const DEFAULT_DESCRIPTION =
  typeof document !== "undefined"
    ? document.head
        .querySelector('meta[name="description"]')
        ?.getAttribute("content") || ""
    : "";

/**
 * Sets the document <title>, the <meta name="description"> and a single
 * <link rel="canonical"> for the current page, writing straight into <head>.
 *
 * Why not react-helmet: in this project's React 18 + CRA build, react-helmet
 * (and react-helmet-async) inject nothing into <head> on initial page load —
 * verified in a production build — which is exactly the moment a crawler reads
 * the page. This hook runs in a useEffect and is dependency-free, so it applies
 * reliably in both dev and prod.
 *
 * The canonical is built from CONST_SITE_URL (the production origin), never from
 * window.location, so it always points at https://sarcasm.co and never leaks
 * the dev host or query strings (utm, fbclid) into the tag.
 *
 * @param {string} path  absolute site path, e.g. "/about"
 * @param {string} [title] full document title
 * @param {string} [description] meta description; omitted leaves the current one
 */
export default function useCanonical(path, title, description) {
  useEffect(() => {
    if (title) document.title = title;

    const nextDescription = description || DEFAULT_DESCRIPTION;
    let meta = document.head.querySelector('meta[name="description"]');
    if (nextDescription) {
      // Update the existing node in place rather than appending a second one.
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", nextDescription);
    } else if (meta) {
      // Nothing to show for this page. Remove the tag rather than leave the
      // PREVIOUS page's description behind to describe this one — a wrong
      // description is worse than none, since a search engine will otherwise
      // quote it verbatim. (index.html currently has its default commented
      // out, so DEFAULT_DESCRIPTION is empty and this is the branch a page
      // without its own description takes.)
      meta.remove();
    }

    const href = `${constant.CONST_SITE_URL}${path}`;
    // Reuse the existing canonical link if one is already in <head> so
    // navigating between pages updates it in place instead of stacking
    // duplicates.
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
  }, [path, title, description]);
}
